import SearchIcon from '@mui/icons-material/Search';
import { Button, Divider, Radio, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { Hybrid } from '../hybrids/hybrid-card';

/**
 * "Buscá un híbrido para comparar" — picks one more hybrid to add to the density comparison.
 * Only hybrids that aren't already selected are listed.
 */
function AddHybridModal({
    open,
    onClose,
    onAdd,
    candidates,
}: {
    open: boolean;
    onClose: () => void;
    onAdd: (id: number) => void;
    candidates: Hybrid[];
}) {
    const units = useUnitProvider();
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return candidates;
        return candidates.filter((hybrid) => hybrid.catalogName?.toLowerCase().includes(term));
    }, [candidates, search]);

    const close = () => {
        setSearch('');
        setSelectedId(undefined);
        onClose();
    };

    return (
        <ModalV2
            open={open}
            onClose={close}
            title={_('Buscá un híbrido para comparar')}
            maxWidth='sm'
            actions={
                <Stack direction='row' justifyContent='flex-end' width='100%'>
                    <Button
                        variant='contained'
                        color='primary'
                        disabled={selectedId === undefined}
                        onClick={() => {
                            if (selectedId !== undefined) onAdd(selectedId);
                            close();
                        }}
                        data-testid='add-hybrid-confirm'
                    >
                        {_('Agregar')}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2} p={2}>
                <TextField
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={_('Busca por nombre de híbrido')}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.mui.inactive' }} /> }}
                    fullWidth
                />

                <Stack direction='row' spacing={2} px={1}>
                    <Typography variant='caption' fontWeight={700} color='primary' width='50%'>
                        {_('Híbridos recomendados')}
                    </Typography>
                    <Typography variant='caption' fontWeight={700} color='primary'>
                        {_('Rendimiento estimado')}
                    </Typography>
                </Stack>

                <Stack sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {filtered.length === 0 && (
                        <Typography variant='body2' color='primary.mui.inactive' p={2}>
                            {_('No encontramos híbridos con ese nombre.')}
                        </Typography>
                    )}
                    {filtered.map((hybrid) => {
                        const estimated = hybrid.recommended?.yield
                            ? units.toYield(hybrid.recommended.yield, hybrid.brand?.type || 'corn', 0)
                            : undefined;
                        return (
                            <Stack key={hybrid.id}>
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    spacing={2}
                                    onClick={() => setSelectedId(hybrid.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: (theme) =>
                                            selectedId === hybrid.id ? theme.palette.action.hover : undefined,
                                    }}
                                >
                                    <Stack direction='row' alignItems='center' width='50%'>
                                        <Radio checked={selectedId === hybrid.id} color='primary' />
                                        <Typography variant='body2' color='primary'>
                                            {hybrid.catalogName}
                                        </Typography>
                                    </Stack>
                                    <Typography variant='body2' color='primary'>
                                        {estimated ? `${estimated.localeValue} ${estimated.unit}` : '-'}
                                    </Typography>
                                </Stack>
                                <Divider />
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>
        </ModalV2>
    );
}

export default AddHybridModal;
